import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress,
  Divider,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Folder as FolderIcon,
  Edit as EditIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import api from '../services/api';
import PageHeader from '../components/PageHeader';

function StorageSettings() {
  const theme = useTheme();
  const [storageInfo, setStorageInfo] = useState(null);
  const [storageConfig, setStorageConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    source_path: '',
    output_path: '',
    overlay_path: '',
    temp_path: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchStorageInfo();
  }, []);

  const fetchStorageInfo = async () => {
    try {
      setLoading(true);
      // Fetch both storage info and config
      const [infoResponse, configResponse] = await Promise.all([
        api.get('/api/storage/info'),
        api.get('/api/storage/config')
      ]);
      setStorageInfo(infoResponse.data);
      setStorageConfig(configResponse.data);
      setError('');
    } catch (_err) {
      setError('Failed to load storage information');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = () => {
    if (storageConfig) {
      setEditForm({
        source_path: storageConfig.source_path,
        output_path: storageConfig.output_path,
        overlay_path: storageConfig.overlay_path,
        temp_path: storageConfig.temp_path
      });
      setEditDialogOpen(true);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      // Only send fields that have changed
      const updates = {};
      if (editForm.source_path !== storageConfig.source_path) {
        updates.source_path = editForm.source_path;
      }
      if (editForm.output_path !== storageConfig.output_path) {
        updates.output_path = editForm.output_path;
      }
      if (editForm.overlay_path !== storageConfig.overlay_path) {
        updates.overlay_path = editForm.overlay_path;
      }
      if (editForm.temp_path !== storageConfig.temp_path) {
        updates.temp_path = editForm.temp_path;
      }
      
      // Only send request if there are actual changes
      if (Object.keys(updates).length === 0) {
        setEditDialogOpen(false);
        return;
      }
      
      const response = await api.put('/api/storage/config', updates);
      setStorageConfig(response.data);
      setEditDialogOpen(false);
      // Refresh storage info
      await fetchStorageInfo();
    } catch (err) {
      setError('Failed to update storage configuration: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <PageHeader title="Storage Settings" />

      <Grid container spacing={3}>
        {/* Storage Paths with Disk Usage */}
        <Grid size={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FolderIcon /> Storage Paths & Disk Usage
              </Typography>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEditClick}
                size="small"
              >
                Edit Paths
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Map through each storage type */}
              {['source', 'output', 'overlay', 'temp'].map((pathType) => {
                const path = storageConfig?.[`${pathType}_path`] || storageInfo?.[`${pathType}_path`];
                const diskInfo = storageInfo?.disk_info_per_path?.find(disk => 
                  disk.paths.includes(pathType)
                );
                
                return (
                  <Box key={pathType}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {pathType === 'source' && 'Source Files Directory'}
                      {pathType === 'output' && 'Output Files Directory'}
                      {pathType === 'overlay' && 'Watermark Overlays Directory'}
                      {pathType === 'temp' && 'Temporary Files Directory'}
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2,
                      bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                      p: 2,
                      borderRadius: 1,
                    }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body1" sx={{ 
                          fontFamily: 'monospace',
                          color: theme.palette.text.primary,
                          overflowX: 'auto',
                          wordBreak: 'break-all'
                        }}>
                          {path}
                        </Typography>
                      </Box>
                      
                      {diskInfo && (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 3,
                          flexShrink: 0,
                          borderLeft: 1,
                          borderColor: 'divider',
                          pl: 3
                        }}>
                          <Box sx={{ minWidth: 200 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={diskInfo.percentage_used}
                              sx={{ height: 8, borderRadius: 4, mb: 0.5 }}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" color="text.secondary">
                                {diskInfo.percentage_used.toFixed(1)}% used
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatBytes(diskInfo.free)} free
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ textAlign: 'right', minWidth: 120 }}>
                            <Typography variant="body2" color="text.secondary">
                              {formatBytes(diskInfo.used)} / {formatBytes(diskInfo.total)}
                            </Typography>
                          </Box>
                          {/* File Statistics */}
                          <Box sx={{ 
                            textAlign: 'right', 
                            minWidth: 150,
                            borderLeft: 1,
                            borderColor: 'divider',
                            pl: 3
                          }}>
                            {storageInfo?.files?.[pathType] ? (
                              <>
                                <Typography variant="body2" color="text.secondary">
                                  {storageInfo.files[pathType].count} files
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatBytes(storageInfo.files[pathType].total_size)}
                                </Typography>
                              </>
                            ) : (
                              <>
                                <Typography variant="body2" color="text.secondary">
                                  -
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  -
                                </Typography>
                              </>
                            )}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Grid>


      </Grid>

      {/* Edit Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        disablePortal={false}
        keepMounted={false}
      >
        <DialogTitle>Edit Storage Paths</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              id="source-path"
              name="source_path"
              label="Source Files Directory"
              value={editForm.source_path}
              onChange={(e) => setEditForm({ ...editForm, source_path: e.target.value })}
              fullWidth
              helperText="Where uploaded video files are stored"
              FormHelperTextProps={{
                sx: { textAlign: 'center' }
              }}
            />
            <TextField
              id="output-path"
              name="output_path"
              label="Output Files Directory"
              value={editForm.output_path}
              onChange={(e) => setEditForm({ ...editForm, output_path: e.target.value })}
              fullWidth
              helperText="Where watermarked videos are saved"
              FormHelperTextProps={{
                sx: { textAlign: 'center' }
              }}
            />
            <TextField
              id="overlay-path"
              name="overlay_path"
              label="Watermark Overlays Directory"
              value={editForm.overlay_path}
              onChange={(e) => setEditForm({ ...editForm, overlay_path: e.target.value })}
              fullWidth
              helperText="Where graphic overlay files are stored"
              FormHelperTextProps={{
                sx: { textAlign: 'center' }
              }}
            />
            <TextField
              id="temp-path"
              name="temp_path"
              label="Temporary Files Directory"
              value={editForm.temp_path}
              onChange={(e) => setEditForm({ ...editForm, temp_path: e.target.value })}
              fullWidth
              helperText="For temporary processing files"
              FormHelperTextProps={{
                sx: { textAlign: 'center' }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveConfig}
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default StorageSettings;